import { useCallback, useState } from 'react';

import type { Filter } from 'Types/filter';

import { getActiveFilterCount } from '../FilterBarV2.helpers';

import { useFilterCountPreview } from './useFilterCountPreview';

interface UseFilterDraftOptions {
	initial: Filter;
	initialCount: Nullable<number>;
}

interface UseFilterDraftReturn {
	clearAll: () => void;
	count: Nullable<number>;
	hasActiveFilters: boolean;
	isCountLoading: boolean;
	localFilters: Filter;
	setValue: (arg: { id: string; value: unknown }) => void;
	unsetValue: (arg: { id: string }) => void;
}

/**
 * Local draft of the filter store for the All Filters modal. Changes apply
 * to the draft immediately; a debounced count fetch keeps the "Show N vehicles"
 * button current. The caller commits the draft to the real store on Apply.
 */
export const useFilterDraft = ({ initial, initialCount }: UseFilterDraftOptions): UseFilterDraftReturn => {
	const [localFilters, setLocalFilters] = useState<Filter>(initial);

	const { count: previewCount, isFetching: isCountLoading } = useFilterCountPreview({
		enabled: true,
		filter: localFilters,
	});

	const count = previewCount ?? initialCount;

	const setValue = useCallback(({ id, value }: { id: string; value: unknown }) => {
		setLocalFilters((prev) => ({ ...prev, [id]: value }));
	}, []);

	const unsetValue = useCallback(({ id }: { id: string }) => {
		setLocalFilters((prev) => ({ ...prev, [id]: null }));
	}, []);

	const clearAll = useCallback(() => {
		setLocalFilters((prev) => {
			const cleared: Filter = { ...prev };
			for (const key of Object.keys(cleared) as (keyof Filter)[]) {
				const val = cleared[key];
				if (Array.isArray(val)) {
					(cleared[key] as unknown[]) = [];
				} else if (typeof val === 'boolean') {
					(cleared[key] as boolean) = false;
				} else if (typeof val === 'number') {
					(cleared[key] as Nullable<number>) = null;
				}
			}
			return cleared;
		});
	}, []);

	return {
		clearAll,
		count,
		hasActiveFilters: getActiveFilterCount(localFilters) > 0,
		isCountLoading,
		localFilters,
		setValue,
		unsetValue,
	};
};
